import { Target, Zap, ListTodo, TrendingUp, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface PriorityStatsProps {
    total: number;
    totalFocus: number;
    totalQueue: number;
    completed: number;
}

interface StatCardProps {
    icon: LucideIcon;
    value: number;
    label: string;
    iconClass: string;
    iconBg: string;
    accentClass: string;
    highlight?: boolean;
    valueClass?: string;
}

function StatCard({ icon: Icon, value, label, iconClass, iconBg, accentClass, highlight, valueClass }: StatCardProps) {
    return (
        <div className={cn(
            "relative overflow-hidden rounded-xl border bg-card p-5 transition-all duration-200",
            highlight ? "border-primary/30 shadow-sm" : "border-border",
        )}>
            <div className={cn("absolute inset-x-0 top-0 h-[3px]", accentClass)} />
            <div className="flex items-center gap-3">
                <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", iconBg)}>
                    <Icon className={cn("h-5 w-5", iconClass)} />
                </div>
                <div>
                    <p className={cn("text-2xl font-bold", valueClass)}>{value}</p>
                    <p className="text-sm text-muted-foreground">{label}</p>
                </div>
            </div>
        </div>
    );
}

export const PriorityStats = ({ total, totalFocus, totalQueue, completed }: PriorityStatsProps) => {
    const focusAtLimit = totalFocus >= 1;
    const queueAtLimit = totalQueue >= 2;

    return (
        <div className="mb-8 grid gap-4 sm:grid-cols-4">
            <StatCard
                icon={Target}
                value={total}
                label="Total Priorities"
                iconClass="text-primary"
                iconBg="bg-primary/10"
                accentClass="bg-primary/50"
            />
            <StatCard
                icon={Zap}
                value={totalFocus}
                label="Focus"
                iconClass={focusAtLimit ? "text-violet-600 dark:text-violet-400" : "text-primary"}
                iconBg={focusAtLimit ? "bg-violet-100 dark:bg-violet-950" : "bg-primary/10"}
                accentClass={focusAtLimit ? "bg-violet-500" : "bg-primary/30"}
                highlight={focusAtLimit}
                valueClass={focusAtLimit ? "text-violet-600 dark:text-violet-400" : undefined}
            />
            <StatCard
                icon={ListTodo}
                value={totalQueue}
                label="Queue"
                iconClass={queueAtLimit ? "text-blue-600 dark:text-blue-400" : "text-info"}
                iconBg={queueAtLimit ? "bg-blue-100 dark:bg-blue-950" : "bg-info/10"}
                accentClass={queueAtLimit ? "bg-blue-500" : "bg-info/30"}
                highlight={queueAtLimit}
                valueClass={queueAtLimit ? "text-blue-600 dark:text-blue-400" : undefined}
            />
            <StatCard
                icon={TrendingUp}
                value={completed}
                label="Completed"
                iconClass="text-emerald-600 dark:text-emerald-400"
                iconBg="bg-emerald-100 dark:bg-emerald-950"
                accentClass="bg-emerald-500"
            />
        </div>
    );
};
